import {
  CaretRightOutlined,
  CheckCircleFilled,
  LockFilled,
  PlusCircleFilled,
  QuestionCircleOutlined,
  SearchOutlined,
  WarningFilled,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons';
import { acroTargetExists } from '@bmo/form-core';
import { Button, Checkbox, Empty, Input, Popconfirm, Segmented, Select, Tag, Tooltip } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { EMPTY_GUID, type PdfFieldMapping, workflowsApi } from '@/api/workflows';
import { useRoles } from '@/auth/useRoles';
import { BmoModal } from '@/components/BmoModal';
import { PageHeader } from '@/components/PageHeader';
import { StatusTag } from '@/components/StatusTag';
import { handleApiError } from '@/lib/handleApiError';
import { message } from '@/lib/message';

import { lockBannerCopy, reclaimConfirmCopy } from './lockBannerCopy';
import { DEFAULT_ZOOM_INDEX, PdfAcroViewer, ZOOM_STEPS } from './PdfAcroViewer';
import {
  collectMappableFields,
  collectPdfEntryOptions,
  columnSlot,
  type MappableField,
} from './pdfFieldMapping';
import { buildOutline, filterFields, initialExpandedStep } from './pdfMappingOutline';
import {
  ADDENDUM_DEFAULT_JOIN,
  ADDENDUM_JOIN_CHOICES,
  JOIN_CHOICES,
  segmentChoicesFor,
} from './pdfSegmentOptions';
import { useWorkflowEditorSession } from './WorkflowEditorSession';

/** A row carries a mapping when it has at least one AcroForm handle. */
function isMapped(f: MappableField): boolean {
  // A column's handles are keyed by PART, so a date column split across year /
  // month / day boxes has no whole-value handle at all — testing `internalName`
  // alone reported it as unmapped while three boxes filled from it.
  return f.kind === 'column'
    ? f.columnMappings.length > 0 || Boolean(f.internalName)
    : f.mappings.length > 0;
}

/** Identity of one AcroForm box a mapping targets — document, name, instance. */
function boxKey(m: PdfFieldMapping): string {
  return `${m.pdfEntryId}|${m.instanceIndex ?? ''}|${m.acroFieldName}`;
}

export function PdfFieldMappingPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { oid } = useRoles();
  const isFr = i18n.language.startsWith('fr');

  const goToDetails = useCallback(
    () => navigate(id ? `/originator/workflows/${id}` : '/originator/dashboard'),
    [navigate, id],
  );
  const { workflow, loading, lockBlock, reclaim, patchWorkflow } = useWorkflowEditorSession();

  const [activeKey, setActiveKey] = useState<string | null>(null);
  // Repeatable groups are collapsed by default (this Set holds the EXPANDED ones),
  // so a section/row that expands into many indexed rows doesn't flood the list.
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const [fieldQuery, setFieldQuery] = useState('');
  const [unmappedOnly, setUnmappedOnly] = useState(false);
  /** Null until the outline first has data, so the auto-open runs exactly once. */
  const [didAutoExpand, setDidAutoExpand] = useState(false);
  const [selectedPdfId, setSelectedPdfId] = useState<string | null>(null);
  const [lang, setLang] = useState<'en' | 'fr'>(isFr ? 'fr' : 'en');
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  // AcroForm names the CURRENTLY SELECTED document actually offers. Empty until
  // the viewer has parsed; a mapping is only flagged as missing once we know.
  const [pdfNames, setPdfNames] = useState<Set<string>>(new Set());
  // Zoom lives here, not in the viewer, so its control can sit in the toolbar
  // — inside the scrolling area it slid out of view as soon as the user panned
  // a magnified page sideways.
  const [zoomIndex, setZoomIndex] = useState(DEFAULT_ZOOM_INDEX);
  const zoom = ZOOM_STEPS[zoomIndex] ?? 1;
  const [helpOpen, setHelpOpen] = useState(false);

  // Recomputed when the selected PDF changes: a repeatable column's click-to-map
  // handle is the one authored against the document on screen, so switching
  // documents genuinely changes which rows read as mapped.
  const mappable = useMemo<MappableField[]>(
    () => (workflow ? collectMappableFields(workflow, isFr, selectedPdfId) : []),
    [workflow, isFr, selectedPdfId],
  );
  const pdfEntries = useMemo(() => (workflow ? collectPdfEntryOptions(workflow) : []), [workflow]);
  const entryLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of pdfEntries) map.set(e.id, e.label);
    return map;
  }, [pdfEntries]);

  // Default the PDF selector to the first uploaded entry once the workflow loads.
  useEffect(() => {
    const first = pdfEntries[0];
    if (selectedPdfId === null && first) setSelectedPdfId(first.id);
  }, [pdfEntries, selectedPdfId]);

  const activeField = useMemo(
    () => mappable.find((f) => f.key === activeKey) ?? null,
    [mappable, activeKey],
  );
  const selectedEntry = pdfEntries.find((e) => e.id === selectedPdfId) ?? null;
  const fileKey = selectedEntry
    ? lang === 'fr'
      ? selectedEntry.frFileKey
      : selectedEntry.enFileKey
    : null;

  // Does a scalar field's mapping apply to the currently-selected PDF? True for
  // a mapping on this exact entry, and for a legacy "all documents" mapping
  // (EMPTY_GUID) so pre-per-entry configs still read as mapped in every PDF.
  const appliesToSelectedPdf = useCallback(
    (pdfEntryId: string) => pdfEntryId === selectedPdfId || pdfEntryId === EMPTY_GUID,
    [selectedPdfId],
  );

  // AcroField name → labels of the OTHER fields mapped to it IN THE SELECTED PDF,
  // for the viewer's "already mapped" styling. Mapping is many-to-many, so a box
  // can carry several; `mappable` is in workflow order, so the list order is the
  // waterfall fill order. The active field's own mapping is excluded so its boxes
  // read as "selected". Scalar fields and repeatable columns alike map per-entry,
  // so both are filtered the same way.
  const assignedNames = useMemo(() => {
    const map = new Map<string, string[]>();
    const add = (name: string, label: string) => {
      const list = map.get(name);
      if (list) list.push(label);
      else map.set(name, [label]);
    };
    for (const f of mappable) {
      if (f.key === activeKey) continue;
      // A count row carries its handles the same way a scalar field does —
      // `mappings`, entry-scoped — so it reads here with them rather than with
      // the column branch below. Tested explicitly rather than by "not a
      // column": a fourth kind must not fall into whichever branch is last.
      if (f.kind === 'field' || f.kind === 'option' || f.kind === 'count') {
        for (const m of f.mappings) {
          if (appliesToSelectedPdf(m.pdfEntryId)) add(m.acroFieldName, f.label);
        }
      } else {
        // A column row carries every document's handles; only the ones this PDF
        // actually receives should grey out a box in it. Before this test, a
        // handle authored on document A marked the box occupied in document B.
        for (const m of f.columnMappings) {
          if (appliesToSelectedPdf(m.pdfEntryId)) add(m.internalName, f.label);
        }
        // An option column keeps its single already-entry-scoped handle.
        if (f.optionValue != null && f.internalName) add(f.internalName, f.label);
      }
    }
    return map;
  }, [mappable, activeKey, appliesToSelectedPdf]);

  // Boxes claimed MORE THAN ONCE within a single form field — counting the
  // field's own mappings and every one of its options together.
  //
  // That is the only situation a language scope can resolve, and the reason it
  // exists: RIF's language radio maps BOTH of its options onto `Check Box4`
  // under both export values, because the two templates swap which box means
  // "English". Everywhere else the templates agree, or disagree in a way the
  // names already settle, and a language picker on the chip would be noise.
  const contestedBoxes = useMemo(() => {
    const count = new Map<string, number>();
    for (const f of mappable) {
      if (f.kind === 'column') continue;
      for (const m of f.mappings) {
        const k = `${f.fieldId}|${m.pdfEntryId}|${m.acroFieldName}`;
        count.set(k, (count.get(k) ?? 0) + 1);
      }
    }
    return new Set([...count.entries()].filter(([, n]) => n > 1).map(([k]) => k));
  }, [mappable]);

  // Fill order, per AcroForm box: every field mapped to the same (PDF entry,
  // AcroForm name, instance), in workflow order. At generation time they fill
  // the box waterfall-style — each writing over the last — so a field's position
  // in this list is where its answer lands in that sequence, and the final entry
  // is the one that survives. Keyed on the raw mapping triple, NOT filtered by
  // the selected PDF, since the tags in the left list show mappings for every
  // document at once.
  const claimantsByBox = useMemo(() => {
    const map = new Map<string, MappableField[]>();
    for (const f of mappable) {
      if (f.kind === 'column') continue;
      for (const m of f.mappings) {
        const key = boxKey(m);
        const list = map.get(key);
        if (list) list.push(f);
        else map.set(key, [f]);
      }
    }
    return map;
  }, [mappable]);

  // The active field's AcroForm names in the selected PDF — the viewer renders
  // these boxes as "selected".
  const activeNames = useMemo(() => {
    const set = new Set<string>();
    if (!activeField) return set;
    if (
      activeField.kind === 'field' ||
      activeField.kind === 'option' ||
      activeField.kind === 'count'
    ) {
      for (const m of activeField.mappings) {
        if (appliesToSelectedPdf(m.pdfEntryId)) set.add(m.acroFieldName);
      }
    } else {
      // Only the handles this document receives render as "selected" — a box
      // mapped on another PDF is not mapped here and must not look like it is.
      for (const m of activeField.columnMappings) {
        if (appliesToSelectedPdf(m.pdfEntryId)) set.add(m.internalName);
      }
      if (activeField.optionValue != null && activeField.internalName) {
        set.add(activeField.internalName);
      }
    }
    return set;
  }, [activeField, appliesToSelectedPdf]);

  // Banner + Reclaim wording both hinge on WHY the mapper lost the lock — a colleague took
  // it, or their own hold timed out. Shared with WorkflowDetailsPage via lockBannerCopy so
  // the two pages can't describe the same state differently.
  const lockCopy = lockBlock ? lockBannerCopy(lockBlock, t, i18n.language) : null;
  const reclaimCopy = lockBlock ? reclaimConfirmCopy(lockBlock, t) : null;

  const mappedCount = mappable.filter(isMapped).length;
  const editable =
    lockBlock === null && (workflow?.status === 'Draft' || workflow?.status === 'Rejected');

  // Search + "unmapped only" narrow the list BEFORE it is grouped, so a step's
  // counter reflects what the panel is actually showing.
  const visibleFields = useMemo(
    () => filterFields(mappable, fieldQuery, unmappedOnly, isMapped),
    // isMapped is module scope — not a dependency.
    [mappable, fieldQuery, unmappedOnly],
  );

  // Step -> section -> row outline. Steps run in form order, which on these
  // forms is the order of the printed PDF, so working top to bottom down this
  // panel tracks working top to bottom through the document.
  const outline = useMemo(() => buildOutline(visibleFields, isMapped), [visibleFields]);

  // Open the first step that still has something to map, so the panel lands on
  // the work rather than on a wall of finished steps. Runs once; after that the
  // originator's own expand/collapse choices stand.
  useEffect(() => {
    if (didAutoExpand || outline.length === 0) return;
    const first = initialExpandedStep(outline);
    if (first) setExpandedSteps(new Set([first]));
    setDidAutoExpand(true);
  }, [outline, didAutoExpand]);

  // Selecting a row from anywhere (including a search hit) must reveal it.
  useEffect(() => {
    if (!activeField) return;
    setExpandedSteps((prev) =>
      prev.has(activeField.stepId) ? prev : new Set(prev).add(activeField.stepId),
    );
  }, [activeField]);

  const toggleStep = useCallback((stepId: string) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(stepId)) next.delete(stepId);
      else next.add(stepId);
      return next;
    });
  }, []);

  const toggleGroup = useCallback((key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handlePick = useCallback(
    async (fieldName: string) => {
      if (!id || !activeField || !editable) return;
      setSaving(true);
      try {
        let removed: boolean;
        if (activeField.kind === 'count') {
          // A COUNTER: the box prints how many rows / instances the repeatable
          // holds. Toggling matches on (entry, box) alone — there is no row,
          // option or part to key on, because the value belongs to the whole
          // repeatable.
          if (!selectedPdfId) return;
          removed = activeField.mappings.some(
            (m) => m.pdfEntryId === selectedPdfId && m.acroFieldName === fieldName,
          );
          if (activeField.countOf === 'instances') {
            // Stored on the SECTION — there is no field under it to hang the
            // handle on, which is the whole reason the section carries its own
            // collection.
            await workflowsApi.toggleSectionCountPdfMapping(
              id,
              activeField.stepId,
              activeField.sectionId,
              { pdfEntryId: selectedPdfId, acroFieldName: fieldName },
            );
          } else {
            // Stored on the parent RepeatableRow field's own `pdfMappings`,
            // which nothing else reads — so the ordinary toggle endpoint does
            // the job with no new wiring. No instanceIndex: a table's row count
            // belongs to the table, not to a repeatable-section instance.
            await workflowsApi.togglePdfMapping(
              id,
              activeField.stepId,
              activeField.sectionId,
              activeField.rowId,
              activeField.columnId,
              { pdfEntryId: selectedPdfId, acroFieldName: fieldName },
            );
          }
        } else if (activeField.kind === 'field' || activeField.kind === 'option') {
          // Scalar field OR choice option: toggle a per-PDF mapping against the
          // selected entry. Options additionally carry their option value.
          if (!selectedPdfId) return;
          removed = activeField.mappings.some(
            (m) => m.pdfEntryId === selectedPdfId && m.acroFieldName === fieldName,
          );
          if (activeField.kind === 'option') {
            await workflowsApi.toggleOptionPdfMapping(
              id,
              activeField.stepId,
              activeField.sectionId,
              activeField.rowId,
              activeField.columnId,
              {
                optionValue: activeField.optionValue,
                pdfEntryId: selectedPdfId,
                acroFieldName: fieldName,
                instanceIndex: activeField.instanceIndex,
              },
            );
          } else {
            await workflowsApi.togglePdfMapping(
              id,
              activeField.stepId,
              activeField.sectionId,
              activeField.rowId,
              activeField.columnId,
              {
                pdfEntryId: selectedPdfId,
                acroFieldName: fieldName,
                instanceIndex: activeField.instanceIndex,
              },
            );
          }
        } else {
          // Repeatable column: one handle per row PER DOCUMENT, or per
          // (row, option) when the column is a choice, or one per PART when the
          // cell is split across several boxes. Clicking a box already mapped on
          // this row of THIS document clears that handle — matched on the name
          // whatever part it carries, so a second click on the Month box removes
          // Month rather than adding a duplicate whole-value handle beside it. A
          // fresh box lands in the whole-value slot, and the part picker on its
          // chip moves it from there.
          if (!selectedPdfId) return;
          // Scoped to the selected entry: a handle on another document is a
          // different mapping, and clicking here must neither report it as
          // already mapped nor clear it.
          const already = activeField.columnMappings.find(
            (m) => m.internalName === fieldName && m.pdfEntryId === selectedPdfId,
          );
          const isOption = activeField.optionValue != null;
          const hit = isOption ? activeField.internalName === fieldName : already !== undefined;
          removed = hit;
          // A clear must name the entry the handle actually sits on, or it
          // matches nothing: an option row may be showing a legacy
          // all-documents handle, which lives under EMPTY_GUID rather than the
          // selected id. Adding always writes to the document on screen.
          const targetEntryId = hit
            ? ((isOption ? activeField.internalNameEntryId : already?.pdfEntryId) ?? selectedPdfId)
            : selectedPdfId;
          await workflowsApi.setFieldInternalName(
            id,
            activeField.stepId,
            activeField.sectionId,
            activeField.rowId,
            activeField.columnId,
            {
              ...columnSlot(activeField, targetEntryId),
              internalName: hit ? null : fieldName,
              segment: hit ? (already?.segment ?? null) : null,
            },
          );
        }
        // Refetch so the whole aggregate (and lock info) stays fresh; the flatten
        // + assigned-name maps recompute from it.
        const fresh = await workflowsApi.get(id);
        patchWorkflow(fresh);
        // Boxes are many-to-many: say so when this click added the active field
        // alongside others, since the fill then waterfalls in workflow order.
        const alsoMapped = assignedNames.get(fieldName)?.length ?? 0;
        message.success(
          removed
            ? t('pdfMapping.cleared')
            : alsoMapped > 0
              ? t('pdfMapping.mappedShared', { total: alsoMapped + 1 })
              : t('pdfMapping.mapped'),
        );
      } catch (err) {
        handleApiError(err, t, t('pdfMapping.saveFailed'));
      } finally {
        setSaving(false);
      }
    },
    [id, activeField, editable, selectedPdfId, patchWorkflow, assignedNames, t],
  );

  // Remove one already-saved mapping directly from its tag in the left list —
  // no need to re-select the field or the matching PDF. Addresses the passed-in
  // field + mapping (not the active field / selected PDF), and reuses the same
  // idempotent toggle endpoints as handlePick: since the pair exists, toggling
  // clears it. Passing the mapping's own pdfEntryId also clears legacy
  // EMPTY_GUID ("all documents") mappings correctly.
  // Persist which PART of the field's value one mapping writes. Field-level
  // only: per-option mappings target checkbox widgets, which are ticked rather
  // than filled, so a slice has no meaning there.
  const handleSegment = useCallback(
    async (field: MappableField, mapping: PdfFieldMapping, segment: string) => {
      if (!id || !editable || field.kind !== 'field') return;
      setSaving(true);
      try {
        await workflowsApi.setPdfMappingSegment(
          id,
          field.stepId,
          field.sectionId,
          field.rowId,
          field.columnId,
          {
            pdfEntryId: mapping.pdfEntryId,
            acroFieldName: mapping.acroFieldName,
            instanceIndex: mapping.instanceIndex,
            segment: segment === '' ? null : segment,
          },
        );
        const fresh = await workflowsApi.get(id);
        patchWorkflow(fresh);
        message.success(t('pdfMapping.segment.saved'));
      } catch (err) {
        handleApiError(err, t, t('pdfMapping.segment.saveFailed'));
      } finally {
        setSaving(false);
      }
    },
    [id, editable, patchWorkflow, t],
  );

  // Persist WHICH RENDERING of the answer this box receives — the chosen
  // option's label, or the number under the field's own format. Edits a mapping
  // that already exists, like the segment handler above. Field-level only: a
  // per-option handle ticks a checkbox, so there is no rendering to pick.
  const handleValueSource = useCallback(
    async (field: MappableField, mapping: PdfFieldMapping, valueSource: string) => {
      if (!id || !editable || field.kind !== 'field') return;
      const next = valueSource === '' ? null : valueSource;
      if ((mapping.valueSource ?? null) === next) return;
      setSaving(true);
      try {
        await workflowsApi.setPdfMappingValueSource(
          id,
          field.stepId,
          field.sectionId,
          field.rowId,
          field.columnId,
          {
            pdfEntryId: mapping.pdfEntryId,
            acroFieldName: mapping.acroFieldName,
            instanceIndex: mapping.instanceIndex ?? null,
            valueSource: next,
          },
        );
        const fresh = await workflowsApi.get(id);
        patchWorkflow(fresh);
        message.success(t('pdfMapping.valueSource.saved'));
      } catch (err) {
        handleApiError(err, t, t('pdfMapping.valueSource.saveFailed'));
      } finally {
        setSaving(false);
      }
    },
    [id, editable, patchWorkflow, t],
  );

  // Gate this box to ONE option: it writes only while that option is the answer.
  // For a question whose answer is also what gets printed — the sub-agreements'
  // "Is this a bulk request?" prints a sentence into the subscriber boxes on Yes,
  // and ungated it puts the literal "no" in them on No. Field-level only: a
  // per-option handle is already scoped to the option that owns it.
  const handleOptionValue = useCallback(
    async (field: MappableField, mapping: PdfFieldMapping, optionValue: string) => {
      if (!id || !editable || field.kind !== 'field') return;
      const next = optionValue === '' ? null : optionValue;
      if ((mapping.optionValue ?? null) === next) return;
      setSaving(true);
      try {
        await workflowsApi.setPdfMappingOptionValue(
          id,
          field.stepId,
          field.sectionId,
          field.rowId,
          field.columnId,
          {
            pdfEntryId: mapping.pdfEntryId,
            acroFieldName: mapping.acroFieldName,
            instanceIndex: mapping.instanceIndex ?? null,
            optionValue: next,
          },
        );
        const fresh = await workflowsApi.get(id);
        patchWorkflow(fresh);
        message.success(t('pdfMapping.optionGate.saved'));
      } catch (err) {
        handleApiError(err, t, t('pdfMapping.optionGate.saveFailed'));
      } finally {
        setSaving(false);
      }
    },
    [id, editable, patchWorkflow, t],
  );

  // Hold one mapping OUT of the fill-time waterfall, or put it back. This is the
  // check-mark ⇄ plus toggle on the chip: an addendum is applied after the whole
  // walk and appends to whatever won the box, so two fields can share one printed
  // line without the later one wiping the earlier. Field-level only — a
  // per-option handle ticks a checkbox, which cannot be appended to.
  const handleAddendum = useCallback(
    async (field: MappableField, mapping: PdfFieldMapping) => {
      if (!id || !editable || field.kind !== 'field') return;
      const next = !mapping.isAddendum;
      setSaving(true);
      try {
        await workflowsApi.setPdfMappingAddendum(
          id,
          field.stepId,
          field.sectionId,
          field.rowId,
          field.columnId,
          {
            pdfEntryId: mapping.pdfEntryId,
            acroFieldName: mapping.acroFieldName,
            instanceIndex: mapping.instanceIndex ?? null,
            isAddendum: next,
          },
        );
        const fresh = await workflowsApi.get(id);
        patchWorkflow(fresh);
        message.success(t(next ? 'pdfMapping.addendum.on' : 'pdfMapping.addendum.off'));
      } catch (err) {
        handleApiError(err, t, t('pdfMapping.addendum.saveFailed'));
      } finally {
        setSaving(false);
      }
    },
    [id, editable, patchWorkflow, t],
  );

  // Which separator an addendum appends with. Stored in the mapping's own
  // `joinWith`, which the fill path reads for both the deferred and the inline
  // append — the flag decides WHEN the value lands, this decides what sits
  // between it and what was already in the box.
  const handleJoinWith = useCallback(
    async (field: MappableField, mapping: PdfFieldMapping, joinWith: string) => {
      if (!id || !editable || field.kind !== 'field') return;
      const next = joinWith === '' ? null : joinWith;
      if ((mapping.joinWith ?? null) === next) return;
      setSaving(true);
      try {
        await workflowsApi.setPdfMappingJoinWith(
          id,
          field.stepId,
          field.sectionId,
          field.rowId,
          field.columnId,
          {
            pdfEntryId: mapping.pdfEntryId,
            acroFieldName: mapping.acroFieldName,
            instanceIndex: mapping.instanceIndex ?? null,
            joinWith: next,
          },
        );
        const fresh = await workflowsApi.get(id);
        patchWorkflow(fresh);
        message.success(t('pdfMapping.join.saved'));
      } catch (err) {
        handleApiError(err, t, t('pdfMapping.join.saveFailed'));
      } finally {
        setSaving(false);
      }
    },
    [id, editable, patchWorkflow, t],
  );

  // Restrict one mapping to a single language of its entry's document, or clear
  // the restriction. Edits a mapping that already exists, so — like the segment
  // and join handlers — it can neither create nor destroy a handle.
  const handleLanguage = useCallback(
    async (field: MappableField, mapping: PdfFieldMapping, language: string) => {
      if (!id || !editable || field.kind === 'column') return;
      const next = language === '' ? null : (language as 'en' | 'fr');
      if ((mapping.language ?? null) === next) return;
      setSaving(true);
      try {
        await workflowsApi.setPdfMappingLanguage(
          id,
          field.stepId,
          field.sectionId,
          field.rowId,
          field.columnId,
          {
            pdfEntryId: mapping.pdfEntryId,
            acroFieldName: mapping.acroFieldName,
            language: next,
            instanceIndex: mapping.instanceIndex ?? null,
            optionValue: field.kind === 'option' ? field.optionValue : null,
          },
        );
        const fresh = await workflowsApi.get(id);
        patchWorkflow(fresh);
        message.success(t('pdfMapping.language.saved'));
      } catch (err) {
        handleApiError(err, t, t('pdfMapping.language.saveFailed'));
      } finally {
        setSaving(false);
      }
    },
    [id, editable, patchWorkflow, t],
  );

  // Move one repeatable-column handle between parts. Unlike the scalar path —
  // where the segment is a property of a mapping that already exists — a
  // column's handles are keyed BY part, so changing the part means clearing the
  // old slot and writing the new one. Two calls, in that order: writing first
  // would leave a duplicate behind if the clear then failed.
  const handleColumnSegment = useCallback(
    async (
      field: MappableField,
      mapping: { internalName: string; pdfEntryId: string; segment?: string | null },
      segment: string,
    ) => {
      if (!id || !editable || field.kind !== 'column') return;
      const next = segment === '' ? null : segment;
      if ((mapping.segment ?? null) === next) return;
      setSaving(true);
      try {
        // The handle's OWN entry on both calls, not the selected one: a chip can
        // belong to another document (or to the legacy all-documents slot), and
        // re-parting it must clear and rewrite the slot it actually occupies.
        const base = columnSlot(field, mapping.pdfEntryId);
        await workflowsApi.setFieldInternalName(
          id,
          field.stepId,
          field.sectionId,
          field.rowId,
          field.columnId,
          { ...base, internalName: null, segment: mapping.segment ?? null },
        );
        await workflowsApi.setFieldInternalName(
          id,
          field.stepId,
          field.sectionId,
          field.rowId,
          field.columnId,
          { ...base, internalName: mapping.internalName, segment: next },
        );
        const fresh = await workflowsApi.get(id);
        patchWorkflow(fresh);
        message.success(t('pdfMapping.segment.saved'));
      } catch (err) {
        handleApiError(err, t, t('pdfMapping.segment.saveFailed'));
      } finally {
        setSaving(false);
      }
    },
    [id, editable, patchWorkflow, t],
  );

  // Set (or clear) the separator with which one column APPENDS to a box another
  // column already fills. One call, and the endpoint edits a mapping that must
  // already exist — unlike the segment path above it can neither create nor
  // destroy a handle, so a failure here cannot leave a half-written slot.
  const handleColumnJoin = useCallback(
    async (
      field: MappableField,
      mapping: {
        internalName: string;
        pdfEntryId: string;
        segment?: string | null;
        joinWith?: string | null;
      },
      joinWith: string,
    ) => {
      if (!id || !editable || field.kind !== 'column') return;
      const next = joinWith === '' ? null : joinWith;
      if ((mapping.joinWith ?? null) === next) return;
      setSaving(true);
      try {
        await workflowsApi.setRepeatableColumnJoinWith(
          id,
          field.stepId,
          field.sectionId,
          field.rowId,
          field.columnId,
          {
            repeatableColumnId: field.repeatableColumnId,
            rowIndex: field.rowIndex,
            internalName: mapping.internalName,
            optionValue: null,
            segment: mapping.segment ?? null,
            joinWith: next,
            // The chip's own document — the endpoint keys on it exactly as the
            // handle toggle does, so the selected entry would miss the mapping.
            pdfEntryId: mapping.pdfEntryId,
          },
        );
        const fresh = await workflowsApi.get(id);
        patchWorkflow(fresh);
        message.success(t('pdfMapping.join.saved'));
      } catch (err) {
        handleApiError(err, t, t('pdfMapping.join.saveFailed'));
      } finally {
        setSaving(false);
      }
    },
    [id, editable, patchWorkflow, t],
  );

  const handleRemove = useCallback(
    async (
      field: MappableField,
      mapping?: PdfFieldMapping,
      columnSegment?: string | null,
      columnName?: string,
      // The chip's own document. Defaults to the row's handle entry so the
      // single-chip option branch (which passes no column details) still names
      // the entry its handle actually sits on.
      columnEntryId?: string | null,
    ) => {
      if (!id || !editable) return;
      setSaving(true);
      try {
        if (field.kind === 'option') {
          if (!mapping) return;
          await workflowsApi.toggleOptionPdfMapping(
            id,
            field.stepId,
            field.sectionId,
            field.rowId,
            field.columnId,
            {
              optionValue: field.optionValue,
              pdfEntryId: mapping.pdfEntryId,
              acroFieldName: mapping.acroFieldName,
              instanceIndex: mapping.instanceIndex,
            },
          );
        } else if (field.kind === 'field') {
          if (!mapping) return;
          await workflowsApi.togglePdfMapping(
            id,
            field.stepId,
            field.sectionId,
            field.rowId,
            field.columnId,
            {
              pdfEntryId: mapping.pdfEntryId,
              acroFieldName: mapping.acroFieldName,
              instanceIndex: mapping.instanceIndex,
            },
          );
        } else if (field.kind === 'count') {
          // A COUNTER chip. The same toggle that created it removes it, keyed
          // on (entry, box) — no row, option or part is involved. Routed by
          // WHICH repeatable it counts, exactly as the click-to-map path is.
          if (!mapping) return;
          if (field.countOf === 'instances') {
            await workflowsApi.toggleSectionCountPdfMapping(id, field.stepId, field.sectionId, {
              pdfEntryId: mapping.pdfEntryId,
              acroFieldName: mapping.acroFieldName,
              language: mapping.language ?? null,
            });
          } else {
            await workflowsApi.togglePdfMapping(
              id,
              field.stepId,
              field.sectionId,
              field.rowId,
              field.columnId,
              { pdfEntryId: mapping.pdfEntryId, acroFieldName: mapping.acroFieldName },
            );
          }
        } else {
          // Repeatable column. A null name clears the whole
          // (entry, row, option, segment) slot, and a slot can hold SEVERAL
          // handles — a cell that fills one box per language keeps both there.
          // So clear the slot, then put back everything except the chip being
          // removed. Two calls for the common single-handle case, one more per
          // surviving sibling.
          //
          // The ENTRY is part of the slot, so removing a chip on document A
          // leaves document B's handle in the same row untouched — and the
          // survivors put back are only A's, which is why they are filtered on
          // it too. Without that, clearing one document's chip would delete the
          // other's and then restore it into the wrong entry.
          const entryId = columnEntryId ?? field.internalNameEntryId ?? selectedPdfId;
          const segment = columnSegment ?? null;
          const survivors = field.columnMappings.filter(
            (m) =>
              (m.segment ?? null) === segment &&
              m.pdfEntryId === entryId &&
              m.internalName !== columnName,
          );
          // The survivors below are rewritten through this same body, so each
          // goes back into the slot it came out of — without the source a
          // surviving total would be re-authored as a plain row-1 cell handle
          // and would then print a cell.
          const body = { ...columnSlot(field, entryId), segment };
          await workflowsApi.setFieldInternalName(
            id,
            field.stepId,
            field.sectionId,
            field.rowId,
            field.columnId,
            { ...body, internalName: null },
          );
          for (const s of survivors) {
            await workflowsApi.setFieldInternalName(
              id,
              field.stepId,
              field.sectionId,
              field.rowId,
              field.columnId,
              { ...body, internalName: s.internalName },
            );
          }
        }
        const fresh = await workflowsApi.get(id);
        patchWorkflow(fresh);
        message.success(t('pdfMapping.cleared'));
      } catch (err) {
        handleApiError(err, t, t('pdfMapping.saveFailed'));
      } finally {
        setSaving(false);
      }
    },
    [id, editable, selectedPdfId, patchWorkflow, t],
  );

  // One selectable field row. `inGroup` indents it under a collapsible header and
  // shortens the label to "Item N · <field>" (the group header carries the rest).
  const renderRow = (f: MappableField, inGroup: boolean) => {
    const active = f.key === activeKey;
    // The step header and section sub-header above already carry the
    // breadcrumb, so the row prints only its own name — that is what turns a
    // three-line row into a one-line one across 500 of them.
    const remainder = f.leafLabel;
    const display =
      inGroup && f.instanceIndex != null
        ? `${t('pdfMapping.itemLabel', { n: f.instanceIndex })} · ${remainder}`
        : // A derived row belongs to the whole column rather than to any of its
          // numbered rows, so it is named for what it carries instead — the
          // mirror of the "Item N ·" prefix the rows get.
          f.kind === 'column' && f.source === 'total'
          ? `${remainder} · ${t('pdfMapping.columnTotal')}`
          : // Same shape, same reason: a counter belongs to the whole
            // repeatable. The two wordings differ because what is counted
            // does — rows of a table, or instances of a section.
            f.kind === 'count'
            ? `${remainder} · ${t(
                f.countOf === 'rows' ? 'pdfMapping.rowCount' : 'pdfMapping.instanceCount',
              )}`
            : remainder;
    const padLeft = 14 + (inGroup ? 14 : 0) + (f.kind === 'option' ? 14 : 0);
    // Repeatable-column rows only. A per-option handle ticks a checkbox and has
    // no value to slice, so it keeps the single-chip rendering it always had.
    const columnChips = f.kind === 'column' && f.optionValue == null ? f.columnMappings : [];
    // A derived row is excluded for the same reason an option row is: there is
    // no typed value to take a slice of. A column total is a computed number
    // that goes into one box whole.
    const columnChoices =
      f.kind === 'column' && f.optionValue == null && f.source == null
        ? segmentChoicesFor({ type: f.columnType, validate: f.columnValidate })
        : [];
    return (
      <button
        type="button"
        key={f.key}
        onClick={() => setActiveKey(f.key)}
        // A derived row is the one kind whose name does not say where its value
        // comes from — "Total" alone leaves an originator guessing whether they
        // type it. Every other row maps something visible on the form.
        title={
          f.kind === 'column' && f.source === 'total'
            ? t('pdfMapping.columnTotalTooltip')
            : f.kind === 'count'
              ? t(
                  f.countOf === 'rows'
                    ? 'pdfMapping.rowCountTooltip'
                    : 'pdfMapping.instanceCountTooltip',
                )
              : undefined
        }
        style={{
          display: 'block',
          width: '100%',
          textAlign: 'left',
          padding: `10px 14px 10px ${padLeft}px`,
          border: 'none',
          borderBottom: '1px solid #f5f5f5',
          borderLeft: active ? '3px solid #0179C1' : '3px solid transparent',
          background: active ? '#eff8ff' : '#fff',
          cursor: 'pointer',
        }}
      >
        <div style={{ fontSize: 13, color: '#111827', marginBottom: 4 }}>{display}</div>
        {f.kind !== 'column' ? (
          f.mappings.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {f.mappings.map((m, i) => {
                // Shared box → surface where this field sits in the waterfall, so
                // the originator can see whether a later field overwrites it.
                const claimants = claimantsByBox.get(boxKey(m)) ?? [];
                const shared = claimants.length > 1;
                const order = claimants.indexOf(f) + 1;
                const label =
                  m.pdfEntryId === EMPTY_GUID
                    ? m.acroFieldName
                    : `${entryLabelById.get(m.pdfEntryId) ?? '?'} · ${m.acroFieldName}`;
                // A mapping that names a box this document does not have fills
                // NOTHING, silently. That is how a French template shipped with
                // every field empty while every row here showed a green tick.
                // Only judged for the document currently on screen, and only
                // once its names are known.
                // A per-option handle is `fieldName:exportValue` — the PDF holds
                // `Radio#1`, not `Radio#1:MrCheck` — so ask the same question the
                // filler asks rather than testing the raw string, which reported
                // every working option mapping as missing.
                const missingHere =
                  m.pdfEntryId === selectedPdfId &&
                  pdfNames.size > 0 &&
                  !acroTargetExists(pdfNames, m.acroFieldName);
                // An addendum is not IN the waterfall — it lands after all of it —
                // so showing it a position in the queue would be telling the
                // originator something untrue.
                const chipText = m.isAddendum
                  ? `${label} · ${t('pdfMapping.addendum.fillOrder')}`
                  : shared
                    ? `${label} · ${t('pdfMapping.fillOrder', { order })}`
                    : label;
                // Clicking the check mark turns it into a plus: this mapping now
                // APPENDS after the waterfall instead of taking part in it.
                // Offered for scalar fields only — a per-option handle ticks a
                // checkbox, and a checkbox cannot be appended to. A missing box
                // keeps its red warning: the mapping fills nothing at all, so a
                // toggle on it would be noise.
                const addendumToggle = f.kind === 'field' && editable && !saving && !missingHere;
                const stateIcon = missingHere ? (
                  <WarningFilled />
                ) : m.isAddendum ? (
                  <PlusCircleFilled />
                ) : (
                  <CheckCircleFilled />
                );
                const icon = addendumToggle ? (
                  <Tooltip
                    title={t(
                      m.isAddendum ? 'pdfMapping.addendum.isOn' : 'pdfMapping.addendum.isOff',
                    )}
                  >
                    {/* A <span role="button">, not a <button>: the whole row is
                        already a <button> and nesting one is invalid HTML. Stops
                        propagation for the same reason the chip's own close
                        button does — the row's onClick would otherwise fire. */}
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label={t('pdfMapping.addendum.label')}
                      aria-pressed={Boolean(m.isAddendum)}
                      // Named so the chip's shrink rule can skip it — an icon
                      // that is also a button must not be squeezed away by a
                      // long AcroForm name. See global.css.
                      className="pdf-mapping-chip__toggle"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        void handleAddendum(f, m);
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter' && e.key !== ' ') return;
                        e.preventDefault();
                        e.stopPropagation();
                        void handleAddendum(f, m);
                      }}
                    >
                      {stateIcon}
                    </span>
                  </Tooltip>
                ) : (
                  stateIcon
                );
                const tag = (
                  <Tag
                    key={`${m.pdfEntryId}:${m.acroFieldName}:${i}`}
                    color={missingHere ? 'error' : 'green'}
                    icon={icon}
                    closable={editable && !saving}
                    onClose={(e) => {
                      // Prevent antd's optimistic DOM removal (we re-render from the
                      // server refetch) and the row button's own onClick from firing.
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemove(f, m);
                    }}
                    className="pdf-mapping-chip"
                    // Inline, not in the class: AntD v5 injects .ant-tag's own
                    // rules at runtime and they can land after global.css, so a
                    // same-specificity class would lose the `display` battle
                    // intermittently. Inline always wins.
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      maxWidth: '100%',
                      minWidth: 0,
                      overflow: 'visible',
                      marginInlineEnd: 0,
                    }}
                  >
                    <span className="pdf-mapping-chip__label" title={chipText}>
                      {chipText}
                    </span>
                  </Tag>
                );
                // Which PART of the value lands in this box. Only offered for
                // scalar fields whose type/validate actually splits (dates,
                // SIN, phone, postal) — a picker with nothing but "Whole value"
                // in it is noise on a 495-row list.
                const choices =
                  f.kind === 'field'
                    ? segmentChoicesFor({ type: f.fieldType, validate: f.validate })
                    : [];
                const segmentPicker =
                  f.kind === 'field' && editable && choices.length > 1 ? (
                    <Tooltip title={t('pdfMapping.segment.tooltip')}>
                      <Select
                        size="small"
                        value={m.segment ?? ''}
                        disabled={saving}
                        aria-label={t('pdfMapping.segment.label')}
                        // flexShrink 0: the chip gives way, not the control the
                        // originator has to click.
                        style={{ minWidth: 108, flexShrink: 0 }}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(v) => void handleSegment(f, m, v)}
                        options={choices.map((c) => ({
                          value: c.value,
                          label: t(`pdfMapping.segment.${c.labelKey}`),
                        }))}
                      />
                    </Tooltip>
                  ) : null;

                // WHICH RENDERING of the answer lands in this box. Offered
                // only where there is more than one: a field with options can
                // print its label, a Number can print under its own format.
                // Everything else would be a picker with one entry — the same
                // reason the segment picker hides itself.
                const valueSourceChoices =
                  f.kind !== 'field'
                    ? []
                    : f.hasOptions
                      ? ['label']
                      : f.fieldType === 'Number'
                        ? // `number` first: on these forms the currency symbol is
                          // almost always already PRINTED beside the box, and
                          // `formatted` there lands as "$ $250,000.00".
                          ['number', 'formatted']
                        : [];
                const valueSourcePicker =
                  editable && valueSourceChoices.length > 0 ? (
                    <Tooltip title={t('pdfMapping.valueSource.tooltip')}>
                      <Select
                        size="small"
                        value={m.valueSource ?? ''}
                        disabled={saving}
                        aria-label={t('pdfMapping.valueSource.control')}
                        style={{ minWidth: 116, flexShrink: 0 }}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(v) => void handleValueSource(f, m, v)}
                        options={[
                          { value: '', label: t('pdfMapping.valueSource.stored') },
                          ...valueSourceChoices.map((c) => ({
                            value: c,
                            label: t(`pdfMapping.valueSource.${c}`),
                          })),
                        ]}
                      />
                    </Tooltip>
                  ) : null;

                // "Fires only on" — offered on any field-level handle whose
                // field has options. Not hidden when unset, unlike the segment
                // picker: an UNGATED handle on a choice field is the state that
                // silently prints the wrong branch's answer, so the control that
                // fixes it has to be visible before anyone knows to look.
                const optionGatePicker =
                  editable && f.kind === 'field' && f.optionChoices.length > 0 ? (
                    <Tooltip title={t('pdfMapping.optionGate.tooltip')}>
                      <Select
                        size="small"
                        value={m.optionValue ?? ''}
                        disabled={saving}
                        aria-label={t('pdfMapping.optionGate.control')}
                        style={{ minWidth: 128, flexShrink: 0 }}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(v) => void handleOptionValue(f, m, v)}
                        options={[
                          { value: '', label: t('pdfMapping.optionGate.any') },
                          ...f.optionChoices.map((c) => ({
                            value: c.value,
                            label: c.label,
                          })),
                        ]}
                      />
                    </Tooltip>
                  ) : null;

                const languagePicker =
                  editable &&
                  contestedBoxes.has(`${f.fieldId}|${m.pdfEntryId}|${m.acroFieldName}`) ? (
                    <Tooltip title={t('pdfMapping.language.tooltip')}>
                      <Select
                        size="small"
                        value={m.language ?? ''}
                        disabled={saving}
                        aria-label={t('pdfMapping.language.label')}
                        style={{ minWidth: 104, flexShrink: 0 }}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(v) => void handleLanguage(f, m, v)}
                        options={[
                          { value: '', label: t('pdfMapping.language.both') },
                          { value: 'en', label: t('pdfMapping.language.en') },
                          { value: 'fr', label: t('pdfMapping.language.fr') },
                        ]}
                      />
                    </Tooltip>
                  ) : null;

                // What sits between this value and what is already in the box.
                // Only on a chip that is actually an addendum — on every other
                // one the answer would be "nothing, it overwrites", which is not
                // a choice worth putting on a 495-row list. The displayed default
                // is the separator the fill path falls back to, so the dropdown
                // never claims something the generated PDF will not do.
                const addendumJoinPicker =
                  f.kind === 'field' && editable && m.isAddendum ? (
                    <Tooltip title={t('pdfMapping.addendum.joinTooltip')}>
                      <Select
                        size="small"
                        value={m.joinWith ?? ADDENDUM_DEFAULT_JOIN}
                        disabled={saving}
                        aria-label={t('pdfMapping.addendum.joinLabel')}
                        style={{ minWidth: 116, flexShrink: 0 }}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(v) => void handleJoinWith(f, m, v)}
                        options={ADDENDUM_JOIN_CHOICES.map((c) => ({
                          value: c.value,
                          label: t(`pdfMapping.join.${c.labelKey}`),
                        }))}
                      />
                    </Tooltip>
                  ) : null;

                const tagWithSegment =
                  segmentPicker ||
                  valueSourcePicker ||
                  optionGatePicker ||
                  languagePicker ||
                  addendumJoinPicker ? (
                    <span
                      key={`${m.pdfEntryId}:${m.acroFieldName}:${i}:seg`}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        maxWidth: '100%',
                      }}
                    >
                      {tag}
                      {segmentPicker}
                      {valueSourcePicker}
                      {optionGatePicker}
                      {languagePicker}
                      {addendumJoinPicker}
                    </span>
                  ) : (
                    tag
                  );

                if (missingHere) {
                  return (
                    <Tooltip
                      key={`${m.pdfEntryId}:${m.acroFieldName}:${i}`}
                      title={t('pdfMapping.missingInPdf', { name: m.acroFieldName })}
                    >
                      {tagWithSegment}
                    </Tooltip>
                  );
                }

                return shared ? (
                  <Tooltip
                    key={`${m.pdfEntryId}:${m.acroFieldName}:${i}`}
                    title={t('pdfMapping.sharedBoxTooltip', {
                      order,
                      total: claimants.length,
                      fields: claimants
                        .filter((c) => c !== f)
                        .map((c) => c.label)
                        .join(' · '),
                    })}
                  >
                    {tagWithSegment}
                  </Tooltip>
                ) : (
                  tagWithSegment
                );
              })}
            </div>
          ) : (
            <Tag>{t('pdfMapping.unmapped')}</Tag>
          )
        ) : columnChips.length > 0 ? (
          // A column row can hold several handles when its cell is split across
          // boxes, so this renders a list exactly as the scalar branch does.
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {columnChips.map((m, i) => {
              // Which document this handle fills, named exactly as the scalar
              // branch names it — a column row now lists handles for every PDF,
              // so an unlabelled chip could not be told from another document's.
              // A legacy handle carries the EMPTY_GUID sentinel and reads as the
              // bare box name, again matching the scalar chip.
              const chipText = `${
                m.pdfEntryId === EMPTY_GUID
                  ? t('pdfMapping.allDocuments')
                  : (entryLabelById.get(m.pdfEntryId) ?? '?')
              } · ${m.internalName}`;
              const tag = (
                <Tag
                  key={`${m.pdfEntryId}:${m.internalName}:${m.segment ?? ''}:${i}`}
                  color="green"
                  icon={<CheckCircleFilled />}
                  closable={editable && !saving}
                  onClose={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRemove(f, undefined, m.segment ?? null, m.internalName, m.pdfEntryId);
                  }}
                  className="pdf-mapping-chip"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    maxWidth: '100%',
                    minWidth: 0,
                    overflow: 'visible',
                    marginInlineEnd: 0,
                  }}
                >
                  <span className="pdf-mapping-chip__label" title={chipText}>
                    {chipText}
                  </span>
                </Tag>
              );
              // Only where another column of this table fills the SAME box on
              // this row: appending is meaningless anywhere else, and putting
              // the control on every chip would bury the panel.
              const joinPicker =
                m.joinable && editable ? (
                  <Tooltip title={t('pdfMapping.join.tooltip')}>
                    <Select
                      size="small"
                      value={m.joinWith ?? ''}
                      disabled={saving}
                      aria-label={t('pdfMapping.join.label')}
                      style={{ minWidth: 116, flexShrink: 0 }}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(v) => void handleColumnJoin(f, m, v)}
                      options={JOIN_CHOICES.map((c) => ({
                        value: c.value,
                        label: t(`pdfMapping.join.${c.labelKey}`),
                      }))}
                    />
                  </Tooltip>
                ) : null;
              // Same control, same presets as a scalar field — a repeatable date
              // column feeding year / month / day combs is the case it exists for.
              const segmentPicker =
                columnChoices.length > 1 && editable ? (
                  <Tooltip title={t('pdfMapping.segment.tooltip')}>
                    <Select
                      size="small"
                      value={m.segment ?? ''}
                      disabled={saving}
                      aria-label={t('pdfMapping.segment.label')}
                      style={{ minWidth: 108, flexShrink: 0 }}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(v) => void handleColumnSegment(f, m, v)}
                      options={columnChoices.map((c) => ({
                        value: c.value,
                        label: t(`pdfMapping.segment.${c.labelKey}`),
                      }))}
                    />
                  </Tooltip>
                ) : null;
              return segmentPicker || joinPicker ? (
                <span
                  key={`${m.pdfEntryId}:${m.internalName}:${m.segment ?? ''}:${i}:seg`}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%' }}
                >
                  {tag}
                  {segmentPicker}
                  {joinPicker}
                </span>
              ) : (
                tag
              );
            })}
          </div>
        ) : f.internalName ? (
          <Tag
            color="green"
            icon={<CheckCircleFilled />}
            closable={editable && !saving}
            onClose={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleRemove(f);
            }}
            className="pdf-mapping-chip"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              maxWidth: '100%',
              minWidth: 0,
              overflow: 'visible',
            }}
          >
            <span className="pdf-mapping-chip__label" title={f.internalName}>
              {f.internalName}
            </span>
          </Tag>
        ) : (
          <Tag>{t('pdfMapping.unmapped')}</Tag>
        )}
      </button>
    );
  };

  if (loading || !workflow) {
    return <div style={{ minHeight: 460 }} />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
      <PageHeader
        title={t('pdfMapping.title')}
        titleSuffix={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <Tooltip title={t('pdfMapping.helpAria')}>
              <button
                type="button"
                aria-label={t('pdfMapping.helpAria')}
                onClick={() => setHelpOpen(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6b7280',
                  fontSize: 18,
                  lineHeight: 1,
                }}
              >
                <QuestionCircleOutlined />
              </button>
            </Tooltip>
            <StatusTag
              status={workflow.status}
              isActive={workflow.isActive}
              hasPendingApproval={!!workflow.pendingApprovedVersionNumber}
              scope="template"
            />
          </span>
        }
        onBack={goToDetails}
      />

      <BmoModal
        open={helpOpen}
        onCancel={() => setHelpOpen(false)}
        title={t('pdfMapping.helpTitle')}
        footer={
          <Button type="primary" onClick={() => setHelpOpen(false)}>
            {t('common.gotIt')}
          </Button>
        }
      >
        <p style={{ margin: 0, lineHeight: 1.6 }}>{t('pdfMapping.helpIntro')}</p>
        <div
          style={{
            marginTop: 16,
            padding: '12px 14px',
            borderRadius: 10,
            background: '#eff8ff',
            border: '1px solid #bae0fd',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4, color: '#0b4a6f' }}>
            {t('pdfMapping.helpSharedNoteTitle')}
          </div>
          <p style={{ margin: 0, color: '#0b4a6f', lineHeight: 1.6 }}>
            {t('pdfMapping.helpSharedNote')}
          </p>
        </div>
        <div
          style={{
            marginTop: 12,
            padding: '12px 14px',
            borderRadius: 10,
            background: '#fff7ed',
            border: '1px solid #fde68a',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4, color: '#92400e' }}>
            {t('pdfMapping.helpCheckboxNoteTitle')}
          </div>
          <p style={{ margin: 0, color: '#92400e', lineHeight: 1.6 }}>
            {t('pdfMapping.helpCheckboxNote')}
          </p>
        </div>
      </BmoModal>

      {!editable && (
        <div
          role="status"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 6,
            padding: '10px 14px',
            borderRadius: 10,
            background: '#fff7ed',
            border: '1px solid #fde68a',
            color: '#92400e',
            fontSize: 13,
          }}
        >
          {/* The two reasons a mapping session is read-only are not the same
              problem and must not share a message. Saying "it must be in Draft"
              to someone looking at a Draft badge reads as a bug in the tool, and
              hides the actual cause — a colleague's lock, or their own abandoned
              one, which frees itself on a timer they cannot see. */}
          {lockCopy && reclaimCopy ? (
            <>
              <span style={{ fontWeight: 700, color: '#7c2d12' }}>
                <LockFilled style={{ marginInlineEnd: 6 }} />
                {lockCopy.title}
              </span>
              <span>{lockCopy.lines.join(' ')}</span>
              {/* Same owner-only escape the details page offers. Without it this
                  banner is a dead end: the mapper has to know to navigate away,
                  reclaim there, and come back. */}
              {workflow?.ownerOid === oid && (
                <Popconfirm
                  title={reclaimCopy.title}
                  description={reclaimCopy.description}
                  okText={t('workflow.lock.forceReleaseConfirmOk')}
                  cancelText={t('common.cancel')}
                  onConfirm={reclaim}
                >
                  <Button size="small">{reclaimCopy.action}</Button>
                </Popconfirm>
              )}
            </>
          ) : (
            t('pdfMapping.readOnlyStatus', {
              status: workflow?.status ?? '',
            })
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 16, flex: 1, minHeight: 0, height: '90vh' }}>
        {/* Left: form fields to map */}
        <div
          style={{
            width: 360,
            flexShrink: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            overflow: 'hidden',
            height: '80vh',
          }}
        >
          <div style={{ padding: '12px 14px', borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>
              {t('pdfMapping.fieldsTitle')}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
              {t('pdfMapping.mappedProgress', { mapped: mappedCount, total: mappable.length })}
            </div>
            {/* Find-by-name, because on a 500-row form scrolling is not a search
                strategy. Matches the breadcrumb too, so typing a step name keeps
                that whole step. */}
            <Input
              allowClear
              size="small"
              prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
              placeholder={t('pdfMapping.searchFields')}
              value={fieldQuery}
              onChange={(e) => setFieldQuery(e.target.value)}
              style={{ marginTop: 10 }}
            />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                marginTop: 8,
              }}
            >
              <Checkbox
                checked={unmappedOnly}
                onChange={(e) => setUnmappedOnly(e.target.checked)}
                style={{ fontSize: 12 }}
              >
                {t('pdfMapping.unmappedOnly')}
              </Checkbox>
              <Button
                type="link"
                size="small"
                style={{ padding: 0, height: 'auto', fontSize: 12 }}
                onClick={() =>
                  setExpandedSteps((prev) =>
                    prev.size === outline.length
                      ? new Set()
                      : new Set(outline.map((st) => st.stepId)),
                  )
                }
              >
                {expandedSteps.size === outline.length
                  ? t('pdfMapping.collapseAll')
                  : t('pdfMapping.expandAll')}
              </Button>
            </div>
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {mappable.length === 0 ? (
              <div style={{ padding: 24 }}>
                <Empty description={t('pdfMapping.noFields')} />
              </div>
            ) : outline.length === 0 ? (
              <div style={{ padding: 24 }}>
                <Empty description={t('pdfMapping.noMatches')} />
              </div>
            ) : (
              outline.map((step) => {
                // A search is a request to SEE the hits — leaving the matching
                // steps collapsed would make the reader open each one to find
                // what they just searched for.
                const stepOpen = expandedSteps.has(step.stepId) || fieldQuery.trim() !== '';
                return (
                  <div key={`step:${step.stepId}`}>
                    <button
                      type="button"
                      onClick={() => toggleStep(step.stepId)}
                      aria-expanded={stepOpen}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        width: '100%',
                        textAlign: 'left',
                        padding: '11px 14px',
                        border: 'none',
                        borderBottom: '1px solid #e5e7eb',
                        background: stepOpen ? '#eff8ff' : '#f6f7f8',
                        cursor: 'pointer',
                      }}
                    >
                      <CaretRightOutlined
                        style={{
                          fontSize: 10,
                          color: '#6b7280',
                          transition: 'transform 140ms ease',
                          transform: stepOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                        }}
                      />
                      <span
                        style={{
                          flex: 1,
                          minWidth: 0,
                          fontSize: 13,
                          fontWeight: 700,
                          color: '#111827',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {step.label}
                      </span>
                      <span
                        style={{
                          fontSize: 12,
                          flexShrink: 0,
                          // Green once a step is finished — the signal you want
                          // when walking a long form top to bottom.
                          color: step.mapped === step.fields.length ? '#15803d' : '#6b7280',
                          fontWeight: step.mapped === step.fields.length ? 600 : 400,
                        }}
                      >
                        {t('pdfMapping.groupProgress', {
                          mapped: step.mapped,
                          total: step.fields.length,
                        })}
                      </span>
                    </button>
                    {stepOpen &&
                      step.sections.map((section) => (
                        <div key={`section:${step.stepId}:${section.sectionId}`}>
                          {/* A repeatable SECTION is one group spanning the whole
                              section, so its own header already names it — a
                              sub-header above would just say the same thing. */}
                          {!(
                            section.entries.length === 1 && section.entries[0]!.kind === 'group'
                          ) && (
                            <div
                              style={{
                                padding: '7px 14px 7px 28px',
                                background: '#fcfcfd',
                                borderBottom: '1px solid #f0f0f0',
                                fontSize: 11,
                                fontWeight: 600,
                                letterSpacing: 0.4,
                                textTransform: 'uppercase',
                                color: '#6b7280',
                                display: 'flex',
                                gap: 8,
                              }}
                            >
                              <span
                                style={{
                                  flex: 1,
                                  minWidth: 0,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {section.label}
                              </span>
                              <span style={{ flexShrink: 0, letterSpacing: 0 }}>
                                {section.mapped}/{section.fields.length}
                              </span>
                            </div>
                          )}
                          {section.entries.map((entry) =>
                            entry.kind === 'row' ? (
                              renderRow(entry.field, false)
                            ) : (
                              <div key={`group:${step.stepId}:${entry.key}`}>
                                <button
                                  type="button"
                                  onClick={() => toggleGroup(entry.key)}
                                  aria-expanded={expandedGroups.has(entry.key)}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    width: '100%',
                                    textAlign: 'left',
                                    padding: '10px 14px 10px 28px',
                                    border: 'none',
                                    borderBottom: '1px solid #f0f0f0',
                                    background: '#fafafa',
                                    cursor: 'pointer',
                                  }}
                                >
                                  <CaretRightOutlined
                                    style={{
                                      fontSize: 10,
                                      color: '#6b7280',
                                      transition: 'transform 140ms ease',
                                      transform: expandedGroups.has(entry.key)
                                        ? 'rotate(90deg)'
                                        : 'rotate(0deg)',
                                    }}
                                  />
                                  <span
                                    style={{
                                      flex: 1,
                                      minWidth: 0,
                                      fontSize: 13,
                                      fontWeight: 600,
                                      color: '#111827',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {entry.label}
                                  </span>
                                  <span style={{ fontSize: 12, color: '#6b7280', flexShrink: 0 }}>
                                    {t('pdfMapping.groupProgress', {
                                      mapped: entry.mapped,
                                      total: entry.fields.length,
                                    })}
                                  </span>
                                </button>
                                {expandedGroups.has(entry.key) &&
                                  entry.fields.map((f) => renderRow(f, true))}
                              </div>
                            ),
                          )}
                        </div>
                      ))}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right: PDF viewer */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            overflow: 'hidden',
            height: '80vh',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 14px',
              borderBottom: '1px solid #f0f0f0',
              flexWrap: 'wrap',
            }}
          >
            <Select
              style={{ minWidth: 220 }}
              placeholder={t('pdfMapping.selectPdf')}
              value={selectedPdfId ?? undefined}
              onChange={setSelectedPdfId}
              options={pdfEntries.map((e) => ({ value: e.id, label: e.label }))}
            />
            <Segmented
              value={lang}
              onChange={(v) => setLang(v as 'en' | 'fr')}
              options={[
                { label: t('pdfMapping.english'), value: 'en' },
                { label: t('pdfMapping.french'), value: 'fr' },
              ]}
            />
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: '#6b7280' }} />}
              placeholder={t('pdfMapping.searchAcro')}
              style={{ maxWidth: 260 }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {/* Zoom. Grouped tight so it reads as one control, and pinned in
                the toolbar so neither vertical nor horizontal panning of a
                magnified page can scroll it away. */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
              <Button
                size="small"
                type="text"
                icon={<ZoomOutOutlined style={{ fontSize: 13 }} />}
                disabled={zoomIndex === 0}
                aria-label={t('pdfMapping.zoomOut')}
                onClick={() => setZoomIndex((z) => Math.max(0, z - 1))}
              />
              <button
                type="button"
                aria-label={t('pdfMapping.zoomLevel', { percent: Math.round(zoom * 100) })}
                title={t('pdfMapping.zoomReset')}
                onClick={() => setZoomIndex(DEFAULT_ZOOM_INDEX)}
                style={{
                  border: 0,
                  background: 'none',
                  padding: '0 2px',
                  minWidth: 40,
                  fontSize: 12,
                  fontWeight: 600,
                  // Fixed-width digits stop the row jiggling as the label goes
                  // 75% -> 100% -> 125%.
                  fontVariantNumeric: 'tabular-nums',
                  color: '#475569',
                  cursor: 'pointer',
                  lineHeight: 1,
                }}
              >
                {Math.round(zoom * 100)}%
              </button>
              <Button
                size="small"
                type="text"
                icon={<ZoomInOutlined style={{ fontSize: 13 }} />}
                disabled={zoomIndex === ZOOM_STEPS.length - 1}
                aria-label={t('pdfMapping.zoomIn')}
                onClick={() => setZoomIndex((z) => Math.min(ZOOM_STEPS.length - 1, z + 1))}
              />
            </div>
            <div
              style={{
                marginLeft: 'auto',
                fontSize: 12,
                color: activeField ? '#0179C1' : '#6b7280',
              }}
            >
              {activeField
                ? t('pdfMapping.activeHint', { field: activeField.label })
                : t('pdfMapping.selectFieldFirst')}
            </div>
          </div>
          <div style={{ flex: 1, overflow: 'auto', background: '#f3f4f6', padding: 20 }}>
            {pdfEntries.length === 0 ? (
              <Empty description={t('pdfMapping.noPdfs')} style={{ marginTop: 60 }} />
            ) : !fileKey ? (
              <Empty description={t('pdfMapping.noFileForLang')} style={{ marginTop: 60 }} />
            ) : (
              <PdfAcroViewer
                zoom={zoom}
                onNamesParsed={(names) => setPdfNames(new Set(names))}
                // Remount on document / language change so pdf.js state resets cleanly.
                key={`${selectedPdfId}:${lang}`}
                templateId={workflow.id}
                fileKey={fileKey}
                search={search}
                activeNames={activeNames}
                assignedNames={assignedNames}
                disabled={!editable || !activeField || saving}
                onPick={handlePick}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
